import React, { useEffect, useState, useCallback } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { authAPI, articleAPI } from '../utils/authUtils';
import { useNavigate } from 'react-router-dom';
import SafeRichText from '../components/SafeRichText';
import LoadingSpinner from '../components/LoadingSpinner';
import { scrollToForm } from '../utils/adminFormUtils';
import '../styles/adminCommonStyles.css';

// Add animation keyframes
const styleSheet = document.createElement("style");
styleSheet.textContent = `
@keyframes modalSlideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-modal-slide-up {
  animation: modalSlideUp 0.3s ease-out forwards;
}
`;

export default function AdminArticles() {
  // =======================
  // State Declarations
  // =======================
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({
    title: '',
    content: '',
    author: '',
    created_at: ''
  });
  const [addFile, setAddFile] = useState(null);
  const [addError, setAddError] = useState(null);
  const [addLoading, setAddLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    id: '',
    title: '',
    content: '',
    author: '',
    created_at: ''
  });
  // Helper to get API URL (use deployed backend if not localhost)
  const getApiUrl = (path) => {
    const backend =
      window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
        ? (process.env.REACT_APP_API_BASE_URL || '')
        : '/backend/php';
    return backend ? backend + path : path;
  };
  // Authors state
  const [authors, setAuthors] = useState([]);
  const [authorsLoading, setAuthorsLoading] = useState(true);
  const [authorsError, setAuthorsError] = useState(null);
  const [newAuthor, setNewAuthor] = useState('');
  const [showAddAuthor, setShowAddAuthor] = useState(false);
  const [addAuthorError, setAddAuthorError] = useState('');
  const [addAuthorLoading, setAddAuthorLoading] = useState(false);
  // Fetch authors from backend
  const fetchAuthors = useCallback(async () => {
    setAuthorsLoading(true);
    setAuthorsError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(getApiUrl('/api/authors'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!res.ok) throw new Error('Failed to fetch authors');
      const data = await res.json();
      const authorsList = Array.isArray(data) ? data : (Array.isArray(data.data) ? data.data : []);
      const names = authorsList.map(a => a.name);
      setAuthors(names);
      return names; // return names so callers can use them
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to fetch authors';
      setAuthorsError(typeof errorMsg === 'string' ? errorMsg : 'Failed to fetch authors');
      console.error('Authors fetch error:', err);
      return [];
    } finally {
      setAuthorsLoading(false);
    }
  }, []); // no dependencies — getApiUrl is a stable inline function

  useEffect(() => {
    fetchAuthors();
  }, [fetchAuthors]);

  // Refetch articles after authors are loaded to sync with deletions
  useEffect(() => {
    if (!authorsLoading && authors.length >= 0) {
      console.log('[Articles] Authors loaded, syncing articles to handle deleted authors...');
      fetchArticles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorsLoading]);

  const [editError, setEditError] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editFile, setEditFile] = useState(null);
  const [editFilePreviewUrl, setEditFilePreviewUrl] = useState(null);
  const [editFilePreviewMime, setEditFilePreviewMime] = useState(null);
  const [editFilePreviewName, setEditFilePreviewName] = useState(null);
  const [editFilePreviewLoading, setEditFilePreviewLoading] = useState(false);
  const [editFilePreviewError, setEditFilePreviewError] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();

  // =======================
  // Helper Functions
  // =======================
  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const checkUserRole = () => {
    try {
      const hasSuperAdminRole = authAPI.hasRole('superadmin');
      setIsSuperAdmin(hasSuperAdminRole);
    } catch (err) {
      setIsSuperAdmin(false);
    }
  };

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await articleAPI.getArticles();
      setArticles(response.data);
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Authentication failed. Please log in again.');
        authAPI.logout();
        navigate('/admin/login');
      } else {
        const errorMsg = err.response?.data?.message || err.message || 'Failed to fetch publications';
        setError(typeof errorMsg === 'string' ? errorMsg : 'Failed to fetch publications');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    checkUserRole();
    fetchArticles();
  }, [fetchArticles]);

  // =======================
  // Form Handlers
  // =======================
  const handleAddInput = (e) => {
    setAddForm({ ...addForm, [e.target.name]: e.target.value });
  };

  const handleAddAuthor = async (e) => {
    if (e) {
      if (typeof e.preventDefault === 'function') e.preventDefault();
      if (typeof e.stopPropagation === 'function') e.stopPropagation();
    }
    const trimmed = newAuthor.trim();
    if (!trimmed) {
      setShowAddAuthor(false);
      setNewAuthor('');
      return;
    }
    if (authors.includes(trimmed)) {
      // Author already exists — just select it
      if (showAddModal) setAddForm(f => ({ ...f, author: trimmed }));
      if (showEditModal) setEditForm(f => ({ ...f, author: trimmed }));
      setShowAddAuthor(false);
      setNewAuthor('');
      return;
    }
    setAddAuthorError('');
    setAddAuthorLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(getApiUrl('/api/authors'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ name: trimmed })
      });
      let apiResult = null;
      try { apiResult = await res.json(); } catch (jsonErr) {}
      console.log('[AddAuthor] POST response status:', res.status, 'body:', apiResult);
      if (!res.ok) {
        throw new Error((apiResult && (apiResult.error || apiResult.message)) || 'Failed to add author');
      }
      // Optimistically add the new author immediately so it shows in the dropdown right away
      setAuthors(prev => prev.includes(trimmed) ? prev : [...prev, trimmed]);
      // Select the new author in the active form
      if (showAddModal) setAddForm(f => ({ ...f, author: trimmed }));
      if (showEditModal) setEditForm(f => ({ ...f, author: trimmed }));
      // Then refetch in background to sync with server
      fetchAuthors();
      setShowAddAuthor(false);
      setNewAuthor('');
    } catch (err) {
      const errorMsg = err.message || 'Failed to add author';
      console.error('[AddAuthor] Error:', err);
      setAddAuthorError(errorMsg); // show inline — do NOT close the input on error
    } finally {
      setAddAuthorLoading(false);
    }
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setAddLoading(true);
    setAddError(null);
    try {
      if (addFile) {
        const fd = new FormData();
        Object.keys(addForm).forEach(k => {
          fd.append(k, addForm[k]);
        });
        fd.append('article_pdf', addFile, addFile.name);
        await articleAPI.createArticle(fd, true);
      } else {
        await articleAPI.createArticle(addForm);
      }
      setShowAddModal(false);
      setAddForm({ title: '', content: '', author: '', created_at: '' });
      setAddFile(null);
      showSuccess('Publication added successfully!');
      fetchArticles();
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to add publication';
      setAddError(typeof errorMsg === 'string' ? errorMsg : 'Failed to add publication');
    } finally {
      setAddLoading(false);
    }
  };

  const handleEditClick = (article) => {
    setEditForm({
      id: article.id,
      title: article.title,
      content: article.content,
      author: article.author || '',
      created_at: article.created_at ? article.created_at.slice(0, 10) : ''
    });
    setShowEditModal(true);
    setEditError(null);
    // Fetch current attached file (if any) for preview/download
    fetchEditFilePreview(article.id);
    // Scroll to edit form
    setTimeout(() => {
      scrollToForm();
    }, 0);
  };

  const fetchEditFilePreview = async (id) => {
    setEditFilePreviewError(null);
    setEditFilePreviewLoading(true);
    setEditFilePreviewUrl(null);
    setEditFilePreviewMime(null);
    setEditFilePreviewName(null);
    try {
      const API_BASE = (process.env.REACT_APP_API_BASE_URL || '/backend/php');
      let fileUrl = `${API_BASE}/api/articles/${id}/file`;
      const resp = await fetch(fileUrl);
      if (!resp.ok) {
        // No file attached or error
        setEditFilePreviewLoading(false);
        return;
      }
      const arrayBuffer = await resp.arrayBuffer();
      const contentType = resp.headers.get('content-type') || '';
      const cd = resp.headers.get('content-disposition');
      let filename = null;
      if (cd) {
        let match = /filename\*=UTF-8''([^;\n]+)/i.exec(cd);
        if (match && match[1]) {
          filename = decodeURIComponent(match[1].trim().replace(/"/g, ''));
        } else {
          match = /filename="?([^";]+)"?/i.exec(cd);
          if (match && match[1]) filename = match[1].trim();
        }
      }
      const blob = new Blob([arrayBuffer], { type: contentType || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      setEditFilePreviewUrl(url);
      setEditFilePreviewMime(contentType);
      setEditFilePreviewName(filename || `article-${id}`);
    } catch (err) {
      console.error('Failed to fetch article file preview:', err);
      setEditFilePreviewError('Failed to load current document');
    } finally {
      setEditFilePreviewLoading(false);
    }
  };

  // Cleanup blob URL when modal closes
  React.useEffect(() => {
    if (!showEditModal) {
      if (editFilePreviewUrl) {
        try { URL.revokeObjectURL(editFilePreviewUrl); } catch (e) {}
      }
      setEditFilePreviewUrl(null);
      setEditFilePreviewMime(null);
      setEditFilePreviewName(null);
      setEditFilePreviewError(null);
      setEditFilePreviewLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showEditModal]);

  const handleEditInput = (e) => {
    setEditForm({ ...editForm, [e.target.name]: e.target.value });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    setEditError(null);
    try {
      // If a new file is attached, submit multipart/form-data
      if (editFile) {
        const fd = new FormData();
        // append textual fields
        Object.keys(editForm).forEach(k => {
          if (k !== 'id') fd.append(k, editForm[k]);
        });
        fd.append('article_pdf', editFile, editFile.name);
        await articleAPI.updateArticle(editForm.id, fd, true);
      } else {
        await articleAPI.updateArticle(editForm.id, editForm);
      }
      setShowEditModal(false);
      showSuccess('Publication updated successfully!');
      fetchArticles();
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to update publication';
      setEditError(typeof errorMsg === 'string' ? errorMsg : 'Failed to update publication');
    } finally {
      setEditLoading(false);
      setEditFile(null);
    }
  };

  const handleDeleteArticle = async (id, title) => {
    if (!window.confirm(`Are you sure you want to delete the publication "${title}"?`)) return;
    setDeleteLoading(id);
    try {
      await articleAPI.deleteArticle(id);
      showSuccess('Publication deleted successfully!');
      fetchArticles();
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to delete publication';
      setError(typeof errorMsg === 'string' ? errorMsg : 'Failed to delete publication');
    } finally {
      setDeleteLoading(null);
    }
  };

  // =======================
  // Render
  // =======================
  return (
    <div className="p-2 sm:p-4 md:p-6 lg:p-8 w-full max-w-[1920px] mx-auto flex flex-col items-center justify-center gap-4 min-h-[calc(100vh-4rem)]">
      <div className="w-full flex flex-col sm:flex-row justify-between items-center mb-4 lg:mb-6 gap-4">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 text-center sm:text-left tracking-tight">
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-600">Publications</span>
        </h2>
        <div className="flex items-center gap-2 sm:gap-4 justify-center sm:justify-end">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${isSuperAdmin ? 'bg-green-100 text-green-800 border border-green-300' : 'bg-blue-100 text-blue-800 border border-blue-300'}`}>
            {isSuperAdmin ? 'Super Admin' : 'Admin'}
          </span>
          <button
            onClick={() => { authAPI.logout(); }}
            className="admin-btn-danger"
          >
            Logout
          </button>
        </div>
      </div>
      {successMessage && (
        <div className="admin-alert-success-backdrop">
          <span className="text-lg">✓</span>
          <span>{successMessage}</span>
        </div>
      )}
      {authorsError && (
        <div className="admin-alert-error">
          Error loading authors: {authorsError}
        </div>
      )}
      {error && (
        <div className="admin-alert-error">
          {error}
        </div>
      )}
      <button
        className="admin-btn-primary-lg mb-4 lg:mb-6"
        onClick={() => {
          setShowAddModal(true);
          setTimeout(() => {
            scrollToForm();
          }, 0);
        }}
      >
          <span className="flex items-center gap-2">
          <span className="text-lg">+</span>
          <span>Add New Publication</span>
        </span>
      </button>
      {showAddModal && (
        <div className="admin-modal-overlay-scroll p-4 sm:p-6 md:p-8">
          <div className="admin-modal-content border border-blue-100 animate-modal-slide-up admin-modal-form" id="add-article-form" style={{ marginTop: '1.5rem' }}>
            <h3 className="admin-modal-title">Add New Publication</h3>
            <form onSubmit={handleAddSubmit}>
              <div className="mb-4">
                <label className="admin-label">Title</label>
                <input
                  type="text"
                  name="title"
                  value={addForm.title}
                  onChange={handleAddInput}
                  className="admin-input"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block mb-2 font-medium text-gray-700">Content</label>
                <Editor
                  value={addForm.content}
                  onEditorChange={val => setAddForm(f => ({ ...f, content: val }))}
                  init={{
                    apiKey: process.env.REACT_APP_TINYMCE_API_KEY,
                    height: 440,
                    menubar: false,
                    branding: false,
                    analytics: false,
                    external_plugins: {},
                    plugins: 'advlist autolink lists link image charmap anchor searchreplace visualblocks code fullscreen insertdatetime media table help wordcount',
                    toolbar:
                      'undo redo | formatselect | bold italic backcolor | \
                      alignleft aligncenter alignright alignjustify | \
                      bullist numlist outdent indent | removeformat | help'
                  }}
                />
              </div>
              <div className="mb-4">
                <label className="admin-label">Author</label>
                <div className="flex gap-2 items-center">
                  <select
                    name="author"
                    value={addForm.author}
                    onChange={handleAddInput}
                    className="admin-input-special"
                    required
                  >
                    <option value="" disabled>Select author</option>
                    {authorsLoading ? (
                      <option>Loading...</option>
                    ) : authors.length === 0 ? (
                      <option disabled>No authors found</option>
                    ) : (
                      authors.map((author, idx) => (
                        <option key={idx} value={author}>{author}</option>
                      ))
                    )}
                  </select>
                  <button
                    type="button"
                    className="px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                    onClick={() => setShowAddAuthor(true)}
                  >
                    + Add Author
                  </button>
                </div>
              </div>
              {/* Move add-author input outside the form to prevent nested form issues */}
              {showAddAuthor && (
                <div className="flex flex-col gap-2 mt-2 mb-4">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newAuthor}
                      onChange={e => { setNewAuthor(e.target.value); setAddAuthorError(''); }}
                      disabled={addAuthorLoading}
                      className="flex-1 border border-gray-300 px-2 py-1 rounded disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder="New author name"
                    />
                    <button
                      type="button"
                      className="admin-btn-primary"
                      onClick={handleAddAuthor}
                      disabled={addAuthorLoading}
                    >{addAuthorLoading ? '⏳ Loading...' : 'Add'}</button>
                    <button
                      type="button"
                      className="admin-btn-secondary"
                      onClick={() => { setShowAddAuthor(false); setNewAuthor(''); setAddAuthorError(''); }}
                      disabled={addAuthorLoading}
                    >Cancel</button>
                  </div>
                  {addAuthorLoading && (
                    <div className="text-sm text-blue-600 bg-blue-50 border border-blue-200 rounded px-2 py-1">
                      ⏳ Adding author, please wait...
                    </div>
                  )}
                  {addAuthorError && (
                    <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1">
                      {addAuthorError}
                    </div>
                  )}
                </div>
              )}
              <div className="mb-4">
                <label className="block mb-2 font-medium text-gray-700">Created At</label>
                              <div className="mb-4">
                                <label className="block mb-2 font-medium text-gray-700">Attach Document (PDF / DOCX / DOC / TXT)</label>
                                <input
                                  type="file"
                                  accept=".pdf,.doc,.docx,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                  onChange={(e) => setAddFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
                                  className="w-full"
                                />
                                <p className="text-xs text-gray-500 mt-1">If you upload a document it will be stored with the article.</p>
                              </div>
                <input
                  type="date"
                  name="created_at"
                  value={addForm.created_at}
                  onChange={handleAddInput}
                  className="admin-input"
                />
              </div>
              {addError && (
                <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded">
                  {addError}
                </div>
              )}
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  className="admin-btn-secondary"
                  onClick={() => {
                    setShowAddModal(false);
                    setAddError(null);
                  }}
                  disabled={addLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="admin-btn-primary-lg"
                  disabled={addLoading}
                >
                  {addLoading ? 'Adding...' : 'Add Publication'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {showEditModal && (
        <div className="admin-modal-overlay-scroll p-4 sm:p-6 md:p-8">
          <div className="admin-modal-content border border-blue-100 animate-modal-slide-up admin-modal-form" id="edit-article-form" style={{ marginTop: '1rem' }}>
            <h3 className="text-lg font-bold mb-2">Edit Publication</h3>
            <form onSubmit={handleEditSubmit}>
              <div className="mb-2">
                <label className="admin-label">Title</label>
                <input
                  type="text"
                  name="title"
                  value={editForm.title}
                  onChange={handleEditInput}
                  className="admin-input"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block mb-2 font-medium text-gray-700">Content</label>
                <Editor
                  value={editForm.content}
                  onEditorChange={val => setEditForm(f => ({ ...f, content: val }))}
                  init={{
                    apiKey: process.env.REACT_APP_TINYMCE_API_KEY,
                    height: 440,
                    menubar: false,
                    branding: false,
                    analytics: false,
                    external_plugins: {},
                    plugins: 'advlist autolink lists link image charmap anchor searchreplace visualblocks code fullscreen insertdatetime media table help wordcount',
                    toolbar:
                      'undo redo | formatselect | bold italic backcolor | \
                      alignleft aligncenter alignright alignjustify | \
                      bullist numlist outdent indent | removeformat | help'
                  }}
                />
              </div>
              <div className="mb-4">
                <label className="block mb-2 font-medium text-gray-700">Author</label>
                <div className="flex gap-2 items-center">
                  <select
                    name="author"
                    value={editForm.author || ''}
                    onChange={handleEditInput}
                    className="w-full border border-blue-400 bg-blue-50 px-4 py-2 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-600 transition-all duration-200 text-gray-800 font-semibold hover:bg-blue-100"
                  >
                    {editForm.author ? (
                      <>
                        <option value={editForm.author}>
                          {editForm.author} {!authors.includes(editForm.author) ? '(current - removed from system)' : '(current)'}
                        </option>
                        <option value="" disabled>---</option>
                        {authors.filter(a => a !== editForm.author).map((author, idx) => (
                          <option key={idx} value={author}>{author}</option>
                        ))}
                      </>
                    ) : (
                      <>
                        <option value="" disabled>Select author</option>
                        {authorsLoading ? (
                          <option disabled>Loading...</option>
                        ) : (
                          authors.map((author, idx) => (
                            <option key={idx} value={author}>{author}</option>
                          ))
                        )}
                      </>
                    )}
                  </select>
                  <button
                    type="button"
                    className="px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                    onClick={() => setShowAddAuthor(true)}
                  >
                    + Add Author
                  </button>
                </div>
                {showAddAuthor && (
                  <div className="flex flex-col gap-2 mt-2 mb-4">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newAuthor}
                        onChange={e => { setNewAuthor(e.target.value); setAddAuthorError(''); }}
                        disabled={addAuthorLoading}
                        className="flex-1 border border-gray-300 px-2 py-1 rounded disabled:bg-gray-100 disabled:cursor-not-allowed"
                        placeholder="New author name"
                      />
                      <button
                        type="button"
                        className="admin-btn-primary"
                        onClick={handleAddAuthor}
                        disabled={addAuthorLoading}
                      >{addAuthorLoading ? '⏳ Loading...' : 'Add'}</button>
                      <button
                        type="button"
                        className="admin-btn-secondary"
                        onClick={() => { setShowAddAuthor(false); setNewAuthor(''); setAddAuthorError(''); }}
                        disabled={addAuthorLoading}
                      >Cancel</button>
                    </div>
                    {addAuthorLoading && (
                      <div className="text-sm text-blue-600 bg-blue-50 border border-blue-200 rounded px-2 py-1">
                        ⏳ Adding author, please wait...
                      </div>
                    )}
                    {addAuthorError && (
                      <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1">
                        {addAuthorError}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="mb-4">
                <label className="admin-label">Created At</label>
                <input
                  type="date"
                  name="created_at"
                  value={editForm.created_at}
                  onChange={handleEditInput}
                  className="admin-input"
                />
              </div>
              <div className="mb-4">
                <label className="block mb-2 font-medium text-gray-700">Attach Document (PDF / DOCX / DOC / TXT)</label>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={(e) => setEditFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
                  className="w-full"
                />
                <p className="text-xs text-gray-500 mt-1">If you upload a document it will replace any existing document stored in the database.</p>
                {/* Current attached document preview / download */}
                <div className="mt-3">
                  {editFilePreviewLoading && (
                    <div className="text-sm text-gray-600">Loading current document preview...</div>
                  )}
                  {editFilePreviewError && (
                    <div className="text-sm text-red-600">{editFilePreviewError}</div>
                  )}
                  {!editFilePreviewLoading && editFilePreviewUrl && (
                    <div className="mt-2 border rounded-md overflow-hidden">
                      {editFilePreviewMime && editFilePreviewMime.includes('pdf') ? (
                        <iframe src={editFilePreviewUrl} title="Current document preview" className="w-full" style={{ height: 320 }} />
                      ) : editFilePreviewMime && editFilePreviewMime.startsWith('text') ? (
                        <iframe src={editFilePreviewUrl} title="Current document preview" className="w-full" style={{ height: 320 }} />
                      ) : (
                        <div className="p-3 flex items-center justify-between">
                          <div>
                            <div className="text-sm font-semibold">{editFilePreviewName || 'Attached document'}</div>
                            <div className="text-xs text-gray-500">{editFilePreviewMime || 'application/octet-stream'}</div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                const a = document.createElement('a');
                                a.href = editFilePreviewUrl;
                                a.download = editFilePreviewName || 'document';
                                document.body.appendChild(a); a.click(); a.remove();
                              }}
                              className="admin-btn-primary"
                            >
                              Download
                            </button>
                          </div>
                        </div>
                      )}
                      <div className="p-2 bg-gray-50 flex items-center justify-between text-xs text-gray-600">
                        <div>Current document: {editFilePreviewName || 'attached file'}</div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              const a = document.createElement('a');
                              a.href = editFilePreviewUrl;
                              a.download = editFilePreviewName || 'document';
                              document.body.appendChild(a); a.click(); a.remove();
                            }}
                            className="underline text-[#3a8b9e]"
                          >Download</button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {editError && (
                <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded">
                  {editError}
                </div>
              )}
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  className="admin-btn-secondary"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditError(null);
                  }}
                  disabled={editLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="admin-btn-primary-lg"
                  disabled={editLoading}
                >
                  {editLoading ? 'Updating...' : 'Update Publication'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {loading && (
        <LoadingSpinner message="Loading publications..." bgColor="bg-white" spinnerColor="border-[#73ab40]" py={12} />
      )}
      {!loading && (
        <div className="admin-table-container">
          <div className="min-w-full overflow-x-auto">
            <table className="admin-table">
            <thead className="admin-table-head">
              <tr>
                <th className="admin-table-header-cell">Title</th>
                <th className="admin-table-header-cell">Content</th>
                <th className="admin-table-header-cell">Author</th>
                <th className="admin-table-header-cell">Actions</th>
              </tr>
            </thead>
            <tbody className="admin-table-body-row">
              {articles.length === 0 ? (
                <tr>
                  <td colSpan="4" className="py-8 px-4 text-center text-gray-500">
                    <div className="text-lg">No publications found</div>
                    <div className="text-sm mt-1">Add your first publication to get started!</div>
                  </td>
                </tr>
              ) : (
                articles.map((article) => (
                  <tr key={article.id} className="hover:bg-blue-50/50 transition-all duration-200 group">
                    <td className="py-2 px-2 font-medium text-gray-900">
                      {article.title && article.title.length > 50 ? article.title.slice(0, 50) + '…' : article.title}
                    </td>
                    <td className="py-2 px-2 max-w-xs truncate whitespace-pre-line break-words" title={article.content}>
                      <SafeRichText content={article.content && article.content.length > 50 ? article.content.slice(0, 50) + '…' : article.content} />
                    </td>
                    <td className="py-2 px-2 font-medium text-gray-900">
                      {article.author && authors.includes(article.author) ? article.author : ''}
                    </td>
                    <td className="py-2 px-2">
                      <div className="flex flex-col gap-1 items-stretch justify-center">
                        <button
                          onClick={() => handleEditClick(article)}
                          className="admin-btn-table-edit"
                        >
                          Edit
                        </button>
                        {isSuperAdmin ? (
                          <button
                            onClick={() => handleDeleteArticle(article.id, article.title)}
                            disabled={deleteLoading === article.id}
                            className="admin-btn-table-danger"
                          >
                            {deleteLoading === article.id ? 'Deleting...' : 'Delete'}
                          </button>
                        ) : (
                          <button
                            disabled
                            className="admin-btn-table-disabled"
                            title="Superadmin role required to delete publications"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}